import React, { useState, useEffect } from 'react';
import { useSite } from '../../context/SiteContext';
import { dbService } from '../../services/firebase';
import { YachtMarina } from '../../types';
import { X, Anchor, MapPin, Plus, Trash2, Check, ShieldCheck } from 'lucide-react';

interface MarinaFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  marinaToEdit: YachtMarina | null;
}

export const MarinaFormModal: React.FC<MarinaFormModalProps> = ({
  isOpen,
  onClose,
  onSave,
  marinaToEdit,
}) => {
  const { activeSite } = useSite();
  const [formData, setFormData] = useState<Partial<YachtMarina>>({
    name: '',
    location: 'Muğla / Türkiye',
    description: '',
    coverImage: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=1200&q=80',
    coordinates: "36°45'N 28°56'E",
    berthCapacity: 400,
    maxDraft: '6m',
    maxLoa: '70m',
    services: ['24/7 Güvenlik', 'Elektrik & Su', 'Yakıt İkmal İstasyonu', 'VIP Lounge', 'Teknik Servis'],
    isFeatured: true,
  });

  const [newService, setNewService] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (marinaToEdit) {
      setFormData(marinaToEdit);
    } else {
      setFormData({
        name: '',
        location: 'Muğla / Türkiye',
        description: '',
        coverImage: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=1200&q=80',
        coordinates: "36°45'N 28°56'E",
        berthCapacity: 400,
        maxDraft: '6m',
        maxLoa: '70m',
        services: ['24/7 Güvenlik', 'Elektrik & Su', 'Yakıt İkmal İstasyonu', 'VIP Lounge', 'Teknik Servis'],
        isFeatured: true,
        isStarter: false,
      });
    }
  }, [marinaToEdit, isOpen]);

  if (!isOpen || !activeSite) return null;

  const handleAddService = () => {
    if (!newService.trim()) return;
    const current = formData.services || [];
    setFormData({ ...formData, services: [...current, newService.trim()] });
    setNewService('');
  };

  const handleRemoveService = (index: number) => {
    const current = formData.services || [];
    setFormData({ ...formData, services: current.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name?.trim()) return;
    setSaving(true);
    try {
      await dbService.saveMarina(activeSite.id, formData);
      onSave();
      onClose();
    } catch (err) {
      console.error('Save marina error:', err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in select-none">
      <div className="bg-white rounded-3xl border border-slate-200 w-full max-w-2xl overflow-hidden shadow-2xl flex flex-col max-h-[90vh]">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 text-cyan-600 flex items-center justify-center">
              <Anchor className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                {marinaToEdit ? 'Marinayı Düzenle' : 'Yeni Marina & Liman Ekle'}
              </h2>
              <p className="text-xs text-slate-500">
                Bağlama noktası, teknik kapasite ve sunduğunuz marina servislerini girin.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Marina Adı */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Marina Adı *
            </label>
            <input
              type="text"
              required
              value={formData.name || ''}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Örn: D-Marin Göcek"
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none"
            />
          </div>

          {/* Konum & Koordinatlar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Konum (Şehir / Bölge)
              </label>
              <input
                type="text"
                value={formData.location || ''}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                placeholder="Örn: Göcek, Fethiye / Muğla"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Koordinatlar
              </label>
              <input
                type="text"
                value={formData.coordinates || ''}
                onChange={(e) => setFormData({ ...formData, coordinates: e.target.value })}
                placeholder="Örn: 36°45'N 28°56'E"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
          </div>

          {/* Açıklama */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Marina Açıklaması
            </label>
            <textarea
              rows={3}
              value={formData.description || ''}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Marinanın olanakları, transfer kolaylıkları ve güvenliği hakkında bilgi..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-500/20 outline-none resize-none"
            />
          </div>

          {/* Teknik Kapasiteler */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Bağlama Kapasitesi (Tekne)
              </label>
              <input
                type="number"
                min={1}
                value={formData.berthCapacity || 400}
                onChange={(e) => setFormData({ ...formData, berthCapacity: Number(e.target.value) })}
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Maksimum Su Çekimi (Draft)
              </label>
              <input
                type="text"
                value={formData.maxDraft || '6m'}
                onChange={(e) => setFormData({ ...formData, maxDraft: e.target.value })}
                placeholder="Örn: 6m"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
                Maksimum Boy (LOA)
              </label>
              <input
                type="text"
                value={formData.maxLoa || '70m'}
                onChange={(e) => setFormData({ ...formData, maxLoa: e.target.value })}
                placeholder="Örn: 70m"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
              />
            </div>
          </div>

          {/* Kapak Görseli */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Marina Fotoğrafı URL'si
            </label>
            <input
              type="url"
              value={formData.coverImage || ''}
              onChange={(e) => setFormData({ ...formData, coverImage: e.target.value })}
              placeholder="https://..."
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-900 text-sm focus:border-cyan-500 outline-none"
            />
            {formData.coverImage && (
              <div className="mt-2 h-28 w-full rounded-xl overflow-hidden border border-slate-200 relative bg-slate-100">
                <img
                  src={formData.coverImage}
                  alt="Marina önizleme"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                />
              </div>
            )}
          </div>

          {/* Hizmetler & Donanımlar */}
          <div>
            <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-1.5">
              Sunulan Marina Hizmetleri
            </label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newService}
                onChange={(e) => setNewService(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddService();
                  }
                }}
                placeholder="Yeni hizmet (Örn: Helikopter Pisti)"
                className="flex-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-slate-900 text-xs focus:border-cyan-500 outline-none"
              />
              <button
                type="button"
                onClick={handleAddService}
                className="px-3 py-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold transition flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" />
                Ekle
              </button>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {formData.services?.map((svc, idx) => (
                <span
                  key={idx}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-cyan-50 text-cyan-800 text-xs font-medium border border-cyan-200"
                >
                  <ShieldCheck className="w-3 h-3 text-cyan-500" />
                  <span>{svc}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveService(idx)}
                    className="text-cyan-400 hover:text-rose-500 ml-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Öne Çıkarılan Marina Switch */}
          <div className="flex items-center gap-2 pt-2">
            <input
              type="checkbox"
              id="marina-featured"
              checked={formData.isFeatured || false}
              onChange={(e) => setFormData({ ...formData, isFeatured: e.target.checked })}
              className="w-4 h-4 rounded text-cyan-600 focus:ring-cyan-500 border-slate-300"
            />
            <label htmlFor="marina-featured" className="text-xs font-semibold text-slate-700 cursor-pointer">
              Ana limanlar vitrininde öne çıkarılsın
            </label>
          </div>
        </form>

        {/* Modal Footer */}
        <div className="p-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl text-slate-600 hover:bg-slate-200 text-xs font-bold transition"
          >
            Vazgeç
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || !formData.name?.trim()}
            className="px-5 py-2.5 rounded-xl bg-cyan-600 hover:bg-cyan-500 text-white text-xs font-bold shadow-lg shadow-cyan-600/20 transition disabled:opacity-50 flex items-center gap-2"
          >
            <Check className="w-4 h-4" />
            <span>{saving ? 'Kaydediliyor...' : 'Marinayı Kaydet'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};
